import axios from 'axios';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useToasts } from 'react-toast-notifications';
import qs from 'qs';
import { useQuery } from 'react-query';
import { makeApiUrl, makeMutationFn } from '../../utils/reactQuery/index';
import { utcToLocal } from '../../utils/date';

import { FailedOrder } from '../../assets/svg/icons';
import apiRoutes from '../../setup/consts/apiRoutes';
import { GetOrdersResponseSingleItem } from '../../setup/apiTypes/orders';
import { OrdersGridFilters, UsePlaceholderPropsReturnType } from './types';
import { downloadFile } from '../../utils';
import { formatDateDashes } from '../../components/atoms/Fields/Datepicker/utils';

export type ReasonBucket = {
  reason: string;
  count: number;
  pct: number;
  trend: 'up' | 'down' | 'stable';
  priorCount: number;
};

type ApiReasonBucket = {
  reason: string;
  count: number;
  percentage: number;
};

type BreakdownApiResponse = {
  response: { buckets: ApiReasonBucket[] };
};

const buildBreakdownUrl = (from?: Date, to?: Date): string => {
  return (
    apiRoutes.FAILED_REASON_BREAKDOWN +
    '?' +
    qs.stringify({
      ...(from ? { from: utcToLocal(from).toISOString() } : {}),
      ...(to ? { to: utcToLocal(to).toISOString() } : {}),
    })
  );
};

export const useFailedReasonBreakdown = (params: {
  from?: Date;
  to?: Date;
}): { breakdown: ReasonBucket[]; isLoading: boolean } => {
  const currentUrl = buildBreakdownUrl(params.from, params.to);

  const windowMs =
    params.from && params.to
      ? params.to.getTime() - params.from.getTime()
      : 0;

  const priorTo = params.from
    ? new Date(params.from.getTime() - 1)
    : undefined;
  const priorFrom = priorTo
    ? new Date(priorTo.getTime() - windowMs)
    : undefined;

  const priorUrl = buildBreakdownUrl(priorFrom, priorTo);

  const { data, isLoading } = useQuery<BreakdownApiResponse>(currentUrl, {
    staleTime: 60_000,
  });

  const { data: priorData } = useQuery<BreakdownApiResponse>(priorUrl, {
    staleTime: 60_000,
    enabled: !!priorFrom && !!priorTo,
  });

  const breakdown: ReasonBucket[] = (
    data?.response?.buckets ?? []
  ).map((bucket) => {
    const prior =
      priorData?.response?.buckets?.find((b) => b.reason === bucket.reason)
        ?.count ?? 0;
    const priorTotal = priorData?.response?.buckets?.reduce(
      (s, b) => s + b.count,
      0
    ) ?? 0;

    let trend: 'up' | 'down' | 'stable' = 'stable';
    if (priorTotal > 0) {
      if (prior === 0 && bucket.count > 0) {
        trend = 'up';
      } else if (prior > 0) {
        const change = (bucket.count - prior) / prior;
        if (change > 0.1) trend = 'up';
        else if (change < -0.1) trend = 'down';
      }
    }

    return {
      reason: bucket.reason,
      count: bucket.count,
      pct: bucket.percentage,
      trend,
      priorCount: prior,
    };
  });

  return { breakdown, isLoading };
};

/* ── Failed-orders money aggregation (M-21 / WOW-4) ────────────────
 * The breakdown endpoint carries counts only; per-order IQD value,
 * city and timestamps exist only on the orders list (pageSize capped
 * at 100 by the BFF). Recoverable revenue, per-reason lost value, city
 * split and the daily trend are therefore aggregated client-side from
 * the real orders — no invented numbers. Capped at MONEY_MAX_PAGES;
 * when the window holds more, `partial` is set and the UI says so.
 */
const MONEY_PAGE_SIZE = 100;
const MONEY_MAX_PAGES = 12;
const MONEY_CONCURRENCY = 4;

export type FailedReasonMoney = {
  reason: string;
  count: number;
  value: number;
  valuePct: number;
};

export type FailedCityMoney = {
  city: string;
  count: number;
  value: number;
};

export type FailedDayMoney = {
  date: string;
  count: number;
  value: number;
};

export type FailedOrdersMoney = {
  totalCount: number;
  coveredCount: number;
  partial: boolean;
  totalValue: number;
  medianMsToFail: number | null;
  reasons: FailedReasonMoney[];
  cities: FailedCityMoney[];
  daily: FailedDayMoney[];
};

type OrdersListResponse = {
  items: GetOrdersResponseSingleItem[];
  total: number;
  pagesCount: number;
};

const fetchFailedOrdersPage = async (
  from: string,
  to: string,
  page: number
): Promise<OrdersListResponse> => {
  const url =
    apiRoutes.ORDERS +
    qs.stringify(
      { from, to, statuses: ['Failed'], page, pageSize: MONEY_PAGE_SIZE },
      { addQueryPrefix: true }
    );

  const { data } = await axios.get<OrdersListResponse>(makeApiUrl(url));

  return data;
};

const toDayKey = (date: Date): string => date.toISOString().slice(0, 10);

const aggregateFailedOrders = (
  rawItems: GetOrdersResponseSingleItem[],
  totalCount: number,
  partial: boolean,
  fromIso: string,
  toIso: string
): FailedOrdersMoney => {
  // Live data can shift pagination between page fetches — dedupe by id.
  const seen = new Set<string>();
  const items = rawItems.filter((o) => {
    if (seen.has(o.id)) return false;
    seen.add(o.id);
    return true;
  });
  let totalValue = 0;
  const reasonMap = new Map<string, { count: number; value: number }>();
  const cityMap = new Map<string, { count: number; value: number }>();
  const dayMap = new Map<string, { count: number; value: number }>();
  const durations: number[] = [];

  items.forEach((order) => {
    const value = order.value || 0;
    totalValue += value;

    const latestAttempt = order.failedAttempts?.[order.failedAttempts.length - 1];
    const reason =
      latestAttempt?.reason?.trim() || latestAttempt?.reasonCode || '—';
    const reasonEntry = reasonMap.get(reason) ?? { count: 0, value: 0 };
    reasonEntry.count += 1;
    reasonEntry.value += value;
    reasonMap.set(reason, reasonEntry);

    const city = (order.city || '').trim() || '—';
    const cityEntry = cityMap.get(city) ?? { count: 0, value: 0 };
    cityEntry.count += 1;
    cityEntry.value += value;
    cityMap.set(city, cityEntry);

    const failedAt = new Date(order.lastUpdatedAt);
    const placedAt = new Date(order.createdAt);
    if (!isNaN(failedAt.getTime())) {
      const dayKey = toDayKey(failedAt);
      const dayEntry = dayMap.get(dayKey) ?? { count: 0, value: 0 };
      dayEntry.count += 1;
      dayEntry.value += value;
      dayMap.set(dayKey, dayEntry);

      if (!isNaN(placedAt.getTime()) && failedAt >= placedAt) {
        durations.push(failedAt.getTime() - placedAt.getTime());
      }
    }
  });

  const daily: FailedDayMoney[] = [];
  // fromIso/toIso are already local-shifted (utcToLocal), so their UTC
  // date parts are the merchant's local calendar days.
  const fromDate = new Date(fromIso);
  const toDate = new Date(toIso);
  const cursor = new Date(
    Date.UTC(
      fromDate.getUTCFullYear(),
      fromDate.getUTCMonth(),
      fromDate.getUTCDate()
    )
  );
  const end = new Date(
    Date.UTC(toDate.getUTCFullYear(), toDate.getUTCMonth(), toDate.getUTCDate())
  );
  while (cursor <= end) {
    const key = toDayKey(cursor);
    const entry = dayMap.get(key) ?? { count: 0, value: 0 };
    daily.push({ date: key, count: entry.count, value: entry.value });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  durations.sort((a, b) => a - b);
  const medianMsToFail = durations.length
    ? durations[Math.floor(durations.length / 2)]
    : null;

  const reasons: FailedReasonMoney[] = Array.from(reasonMap.entries())
    .map(([reason, entry]) => ({
      reason,
      count: entry.count,
      value: entry.value,
      valuePct: totalValue > 0 ? Math.round((entry.value / totalValue) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value);

  const cities: FailedCityMoney[] = Array.from(cityMap.entries())
    .map(([city, entry]) => ({ city, count: entry.count, value: entry.value }))
    .sort((a, b) => b.count - a.count);

  return {
    totalCount,
    coveredCount: items.length,
    partial,
    totalValue,
    medianMsToFail,
    reasons,
    cities,
    daily,
  };
};

export const useFailedOrdersMoney = (params: {
  from?: Date;
  to?: Date;
}): { money: FailedOrdersMoney | undefined; isLoading: boolean } => {
  const fromIso = params.from ? utcToLocal(params.from).toISOString() : undefined;
  const toIso = params.to ? utcToLocal(params.to).toISOString() : undefined;
  const { data, isLoading } = useQuery<FailedOrdersMoney>(
    ['failed-orders-money', fromIso, toIso],
    async () => {
      const first = await fetchFailedOrdersPage(fromIso!, toIso!, 1);
      const items = [...first.items];
      const pagesNeeded = Math.ceil(first.total / MONEY_PAGE_SIZE);
      const pages = Math.min(pagesNeeded, MONEY_MAX_PAGES);

      for (let page = 2; page <= pages; page += MONEY_CONCURRENCY) {
        const batch: Promise<OrdersListResponse>[] = [];
        for (
          let next = page;
          next < page + MONEY_CONCURRENCY && next <= pages;
          next++
        ) {
          batch.push(fetchFailedOrdersPage(fromIso!, toIso!, next));
        }
        (await Promise.all(batch)).forEach((res) => items.push(...res.items));
      }

      return aggregateFailedOrders(
        items,
        first.total,
        pagesNeeded > MONEY_MAX_PAGES,
        fromIso!,
        toIso!
      );
    },
    {
      enabled: !!fromIso && !!toIso,
      staleTime: 5 * 60_000,
      // Multi-page fan-out: default 3 retries × 11 pages ≈ 44 requests
      // during a prod outage; the client default refetchOnMount 'always'
      // would also defeat the 5-min cache on every navigation.
      retry: 1,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  return { money: data, isLoading };
};

export const usePlaceholderProps = (): UsePlaceholderPropsReturnType => {
  const intl = useIntl();

  const title = intl.formatMessage({
    id: 'orders.noFailedOrders',
    defaultMessage: 'No Failed Orders',
  });

  const description = intl.formatMessage({
    id: 'orders.failedOrdersPlaceholderDescription',
    defaultMessage: 'Failed Orders Placeholder Description',
  });

  const Icon = FailedOrder;

  return {
    title,
    description,
    Icon,
  };
};

export const useExportOrders = (): {
  isLoading: boolean;
  handleDownloadOrdersExcelFile: (filters: OrdersGridFilters) => Promise<any>;
} => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { addToast } = useToasts();
  const intl = useIntl();

  const handleDownloadOrdersExcelFile = async (filters: OrdersGridFilters) => {
    try {
      setIsLoading(true);

      const res = await axios
        .get(makeApiUrl(apiRoutes.ORDERS_EXPORT), {
          params: filters,
          paramsSerializer: (params) => qs.stringify(params),
          responseType: 'blob',
        })
        .finally(() => setIsLoading(false));

      if (res.status === 200 && res.data) {
        const file = new Blob([res.data]);

        downloadFile(file, `orders - ${formatDateDashes(new Date())}.xlsx`);
      } else {
        addToast(intl.formatMessage({ id: 'failed' }), {
          appearance: 'error',
        });
      }
    } catch (error) {
      addToast(intl.formatMessage({ id: 'failed' }), {
        appearance: 'error',
      });
    }
  };

  return {
    isLoading,
    handleDownloadOrdersExcelFile,
  };
};

export const useBulkCancelOrders = (): {
  isLoading: boolean;
  bulkCancel: (orderIds: string[], onDone: () => void) => Promise<void>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToasts();

  const bulkCancel = async (orderIds: string[], onDone: () => void) => {
    setIsLoading(true);
    let successCount = 0;
    let failCount = 0;

    await Promise.all(
      orderIds.map(async (orderId) => {
        try {
          const detailsRes = await axios.get(
            makeApiUrl(apiRoutes.ORDER_SUMMARY_ORDER_DETAILS(orderId))
          );
          const fulfillmentId = detailsRes.data?.response?.fulfillmentId;
          if (!fulfillmentId) {
            failCount++;
            return;
          }
          const cancelFn = makeMutationFn(apiRoutes.CANCEL_ORDER(orderId));
          await cancelFn({ data: { fulfillmentId } });
          successCount++;
        } catch {
          failCount++;
        }
      })
    );

    setIsLoading(false);

    if (successCount > 0) {
      addToast(`${successCount} order${successCount !== 1 ? 's' : ''} cancelled`, {
        appearance: 'success',
      });
    }
    if (failCount > 0) {
      addToast(`${failCount} order${failCount !== 1 ? 's' : ''} failed to cancel`, {
        appearance: 'error',
      });
    }

    onDone();
  };

  return { isLoading, bulkCancel };
};
